import { apiAxios } from '@/api'
import {
  fetchIfNeededWrapper,
  fetchWrapper,
  getAllFetchState,
  rejectFetch,
  resolveFetch,
  startFetch,
} from '@/reducers/fetch'
import type { Dispatch, GetState } from '@/utils/typeHelpers'
import { setPermissions } from '../reducers/auth/securityReducer'

export enum ProductFeatureId {
  annualTaxes = 'annual_taxes',
  annualTaxesBusinessFiling = 'annual_taxes_business_filing',
  dedicatedSupport = 'dedicated_support',
  free2ndTaxFiling = 'free_2nd_tax_filing',
  groupPracticeSupport = 'group_practice_support',
  managedBookkeeping = 'managed_bookkeeping',
  payroll = 'payroll',
  payrollIncludedForFreeForSelf = 'payroll_included_for_free_for_self',
  phoneSupport = 'phone_support',
  quarterlyTaxes = 'quarterly_taxes',
  sCorpEligibilitySupport = 's_corp_eligibility_support',
  sCorpComplianceSupport = 's_corp_compliance_support',
  taxConsultation = 'tax_consultation',
  taxConsultationIncludedForFree = 'tax_consultation_included_for_free',
}

export interface FeaturePermission {
  id: ProductFeatureId
  name: string
  description: string
}

export const FETCH_USER_PERMISSIONS_KEY = 'FETCH_USER_PERMISSIONS_KEY'
const USER_PERMISSIONS_URL = '/finances/api/v2/permissions/user'
export const PERMISSIONS_MAX_AGE_MS = 60_000

export const getUserPermissions = () =>
  fetchIfNeededWrapper({
    fetchKey: FETCH_USER_PERMISSIONS_KEY,
    fetchFunction: async (dispatch) => {
      const res = await apiAxios.get<ProductFeatureId[]>(USER_PERMISSIONS_URL)
      dispatch(setPermissions(res.data))
      return res.data
    },
  })

export const FETCH_USER_PERMISSIONS_REFRESH_KEY =
  'FETCH_USER_PERMISSIONS_REFRESH_KEY'

/**
 * Re-reads the cached permission list once it is a minute old, so a rule that depends on
 * the clock reaches a tab that has been open since before it changed. The attempt is
 * tracked under its own fetch key: the layout reads the main key's loading state to lock
 * navigation items, and a second call, or a retry after a failure, waits out the minute.
 * A response is dropped when a newer read of the main list started or finished while it was
 * in flight, so a slow refresh cannot put back a list the customer has since changed.
 * Resolves to the time until the list is next due when it is still fresh, otherwise null.
 */
export const refreshUserPermissionsIfStale =
  () =>
  async (dispatch: Dispatch, getState: GetState): Promise<number | null> => {
    const fetchState = getAllFetchState(getState())
    const loaded = fetchState[FETCH_USER_PERMISSIONS_KEY]
    const refresh = fetchState[FETCH_USER_PERMISSIONS_REFRESH_KEY]
    if (!loaded || loaded.isLoading || refresh?.isLoading) return null
    const lastReadAt = Math.max(
      loaded.receivedAt ?? 0,
      refresh?.receivedAt ?? 0
    )
    const dueIn = lastReadAt + PERMISSIONS_MAX_AGE_MS - Date.now()
    if (dueIn > 0) return dueIn
    const readAtStart = loaded.receivedAt
    dispatch(startFetch(FETCH_USER_PERMISSIONS_REFRESH_KEY))
    try {
      const res = await apiAxios.get<ProductFeatureId[]>(USER_PERMISSIONS_URL)
      const latest = getAllFetchState(getState())[FETCH_USER_PERMISSIONS_KEY]
      const superseded = latest?.isLoading || latest?.receivedAt !== readAtStart
      if (!superseded) dispatch(setPermissions(res.data))
      dispatch(resolveFetch(FETCH_USER_PERMISSIONS_REFRESH_KEY))
    } catch {
      dispatch(
        rejectFetch({
          key: FETCH_USER_PERMISSIONS_REFRESH_KEY,
          error: { message: 'Unable to refresh permissions.' },
        })
      )
    }
    return null
  }

/**
 * Runs the refresh for a mounted screen. A list read just before the screen opened is
 * re-checked once it ages out, so a date that passed in between still reaches the screen.
 * Returns the cleanup for the effect.
 */
export const startUserPermissionsRefresh = (dispatch: Dispatch) => {
  let timer: ReturnType<typeof setTimeout> | undefined
  let stopped = false
  const run = async () => {
    const dueIn = await dispatch(refreshUserPermissionsIfStale())
    if (!stopped && dueIn !== null) timer = setTimeout(run, dueIn)
  }
  run()
  return () => {
    stopped = true
    clearTimeout(timer)
  }
}

export const FETCH_PRODUCT_PERMISSIONS_KEY = 'FETCH_PRODUCT_PERMISSIONS_KEY'
export const getProductPermissions = ({
  productIds,
}: {
  productIds: string[] | undefined
}) =>
  fetchWrapper({
    fetchKey: FETCH_PRODUCT_PERMISSIONS_KEY,
    fetchFunction: async () => {
      if (!productIds || productIds.length === 0) {
        return {}
      }
      const res = await apiAxios.get<Record<string, ProductFeatureId[]>>(
        `/finances/api/v2/permissions/product?productIds=${productIds.join(',')}`
      )
      return res.data
    },
  })
